import type { LogSignature, LogBucket } from "@/hooks/api";
import { cn } from "@/lib/cn";

const nf = new Intl.NumberFormat("nb-NO");

// Row grid is shared by the header and the rows so the columns line up.
const GRID = "grid grid-cols-[60px_minmax(0,1fr)_76px_76px_104px_66px] gap-2.5";

const BUCKET_STYLE: Record<
  LogBucket,
  { badge: string; edge: string; count: string }
> = {
  action: { badge: "bg-admin-danger-950 text-admin-danger-300", edge: "#f87171", count: "text-admin-primary" },
  auth: { badge: "bg-admin-warn-950 text-admin-warn-400", edge: "#8a6a1e", count: "text-admin-text-secondary" },
  noise: { badge: "bg-admin-slate-badge text-admin-text-subtle", edge: "#2a2f3a", count: "text-admin-text-subtle" },
};

export function relativeTime(iso: string): string {
  const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return `${Math.round(secs)} s`;
  if (secs < 3600) return `${Math.round(secs / 60)} min`;
  if (secs < 86400) return `${Math.round(secs / 3600)} t`;
  return `${Math.round(secs / 86400)} d`;
}

/** 12 bars, bottom aligned. A rising signature reddens its newest three. */
function Sparkline({ values, rising }: { values: number[]; rising: boolean }) {
  const max = Math.max(1, ...values);
  return (
    <div className="flex items-end justify-end gap-0.5 h-6">
      {values.map((v, i) => (
        <div
          key={i}
          className="w-1 rounded-[1px]"
          style={{
            height: `${Math.max(2, (v / max) * 24)}px`,
            background: rising
              ? i >= values.length - 3
                ? "#f87171"
                : "#7f3f45"
              : "#333a46",
          }}
        />
      ))}
    </div>
  );
}

function Flag({ children, className }: { children: string; className: string }) {
  return (
    <span
      className={cn(
        "text-[9.5px] font-bold tracking-[0.06em] px-1.5 py-0.5 rounded-[3px] shrink-0",
        className
      )}
    >
      {children}
    </span>
  );
}

/** Real row height, so the list does not jump when data arrives. */
export function SignatureSkeleton() {
  return (
    <div>
      {[62, 48, 70, 40].map((w, i) => (
        <div key={i} className="flex items-center gap-3 px-3.5 py-3 border-b border-admin-slate-divider">
          <div className="w-[34px] h-[18px] rounded bg-admin-card-hover animate-pulse" />
          <div className="flex-1 flex flex-col gap-1.5">
            <div className="h-2.5 rounded bg-admin-card-hover animate-pulse" style={{ width: `${w}%` }} />
            <div className="h-2 rounded bg-admin-card-hover/60 animate-pulse" style={{ width: `${w - 20}%` }} />
          </div>
          <div className="w-11 h-3 rounded bg-admin-card-hover animate-pulse" />
        </div>
      ))}
    </div>
  );
}

interface Props {
  rows: LogSignature[];
  selected: string | null;
  onSelect: (signature: string) => void;
  loading: boolean;
  hiddenLabel: string;
  onShowAll: () => void;
}

export function SignatureTable({
  rows,
  selected,
  onSelect,
  loading,
  hiddenLabel,
  onShowAll,
}: Props) {
  return (
    <div className="bg-admin-card border border-admin-card-border rounded-[10px] overflow-hidden">
      <div
        className={cn(
          GRID,
          "px-3.5 py-2.5 border-b border-admin-card-border text-[10.5px] tracking-[0.06em] text-admin-text-subtle"
        )}
      >
        <div>STATUS</div>
        <div>SIGNATUR</div>
        <div className="text-right">24 T</div>
        <div className="text-right">ANTALL</div>
        <div>OMFANG</div>
        <div className="text-right">SIST</div>
      </div>

      {loading && rows.length === 0 ? (
        <SignatureSkeleton />
      ) : rows.length === 0 ? (
        <div className="py-12 px-6 flex flex-col items-center gap-2.5 text-center">
          <span className="w-2.5 h-2.5 rounded-full bg-admin-mint-400" />
          <div className="text-[15px] font-semibold">Ingen åpne signaturer</div>
          <div className="text-[12.5px] text-admin-text-subtle max-w-[320px] leading-relaxed">
            Alt i «krever handling» er løst eller dempet.
          </div>
        </div>
      ) : (
        rows.map((row) => {
          const style = BUCKET_STYLE[row.bucket];
          const isSelected = selected === row.signature;
          const muted = !!row.mutedUntil && new Date(row.mutedUntil) > new Date();

          return (
            <div
              key={row.signature}
              onClick={() => onSelect(row.signature)}
              className={cn(
                GRID,
                "px-3.5 py-2.5 border-b border-admin-slate-divider cursor-pointer items-center transition-colors duration-[120ms]",
                isSelected ? "bg-admin-card-hover" : "hover:bg-admin-card-hover/40"
              )}
              style={{
                boxShadow: `inset 3px 0 0 ${isSelected ? "#D0A84C" : style.edge}`,
                opacity: muted ? 0.55 : 1,
              }}
            >
              <div>
                <span
                  className={cn(
                    "font-mono text-[11px] font-bold px-1.5 py-[3px] rounded",
                    style.badge
                  )}
                >
                  {row.status ?? "JOBB"}
                </span>
              </div>

              {/* The title is the primary content — it wraps rather than truncates. */}
              <div className="min-w-0 flex flex-col gap-[3px]">
                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                  <span
                    className={cn(
                      "font-mono text-[12.5px] break-all",
                      row.bucket === "noise" ? "text-admin-text-subtle" : "text-admin-text-primary"
                    )}
                  >
                    {row.signature}
                  </span>
                  {row.isNew && <Flag className="bg-admin-primary-active text-admin-primary">NY</Flag>}
                  {muted && <Flag className="bg-admin-slate-badge text-admin-text-subtle">DEMPET</Flag>}
                  {row.resolvedAt &&
                    (new Date(row.lastSeen) <= new Date(row.resolvedAt) ? (
                      <Flag className="bg-admin-mint-950 text-admin-mint-400">LØST</Flag>
                    ) : (
                      // Marked løst, then fired again. The note is still on the
                      // row, so this says "look at what you wrote last time".
                      <Flag className="bg-admin-warn-950 text-admin-warn-400">TILBAKE</Flag>
                    ))}
                </div>
                {row.note && (
                  <div className="text-[11.5px] text-admin-text-subtle line-clamp-1">
                    {row.note}
                  </div>
                )}
              </div>

              <Sparkline values={row.sparkline} rising={row.isRising} />

              <div className={cn("text-right font-mono text-[13px]", style.count)}>
                {nf.format(row.count)}
              </div>

              <div className="text-[11.5px] text-admin-text-secondary truncate">
                {row.jobId
                  ? `jobb ${row.jobId}`
                  : row.users > 0
                    ? `${row.users} bruker${row.users === 1 ? "" : "e"}`
                    : row.context ?? "—"}
              </div>

              <div className="text-right text-[11.5px] text-admin-text-subtle">
                {relativeTime(row.lastSeen)}
              </div>
            </div>
          );
        })
      )}

      <div className="px-3.5 py-2.5 flex items-center gap-3.5 text-[12.5px] text-admin-text-subtle bg-admin-bg">
        <span>{hiddenLabel}</span>
        <span onClick={onShowAll} className="text-admin-primary cursor-pointer hover:underline">
          Vis alt
        </span>
        <div className="flex-1" />
        <span className="font-mono text-[11px]">j / k naviger · e løs · m demp · / søk</span>
      </div>
    </div>
  );
}
